import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, OnDestroy, AfterViewInit, Output, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { DocumentService } from 'src/services/document.service';
import { Store } from '@ngrx/store';
import { SelectedAlphabar } from '../../actions/alphabet-selection.action';

@Component({
  selector: 'app-cps-alpha-bar',
  templateUrl: './cps-alpha-bar.component.html',
  styleUrls: ['./cps-alpha-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CpsAlphaBarComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  public alphabets = ['#', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
  public availableAlphas: string[] = [];
  public selectedAlpha = '';

  public isScrolling = false; 
  public isScrollable = false; 

  private destroy$ = new ReplaySubject<boolean>(1);
  private _scrollOffsetForHeader = 320;
  private lastScrollTop = 0;
  private isClickAction = false;
  private isSelectedAlphaLoadedFromStore = false;
  public contentLoaded = false;

  private isStoreHandled = false;
  private isItemListHandled = false;
  private isViewInitialized = false;  // Flag to track view initialization
  private hasUserSelectedAlpha = false;  // Flag to track if the user has made a selection
  private isDefaultAlphaSet = false;  // Flag to track if default alpha is already set
  private isContentLoadedChecked = false; // Flag to ensure contentLoaded is only checked once

  @Output() public selectedAlphaEvent = new EventEmitter<string>();
  @Input() public document: Document;
  @Input() public identifier!: string;
  @Input() public itemList: any;
  @Input() public isParentPageLoaded: boolean;

  @ViewChild('alphaBar') alphaBar: ElementRef;

  constructor(
    private documentService: DocumentService,
    private store: Store<any>,
    private changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.documentService.getContentLoaded()
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoaded => {
        this.contentLoaded = isLoaded;

        // Only attempt to set default alpha after content is loaded
        if (this.contentLoaded && !this.isContentLoadedChecked) {
          this.isContentLoadedChecked = true;
          this.getItemFromStore();
        }
      });
  }

  ngOnChanges(): void {
    this.getItemFromStore(); 

    if (this.itemList && !this.isItemListHandled) {
      this.availableAlphas = [];
      for (const item of this.itemList) {
        const firstChar = item.title.charAt(0).toUpperCase();
        if (!isNaN(firstChar)) {
          if (!this.availableAlphas.includes('#')) {
            this.availableAlphas.push('#');
          }
        } else if (!this.availableAlphas.includes(firstChar)) {
          this.availableAlphas.push(firstChar);
        }
      }

      this.isItemListHandled = true;
    }
  }

  ngAfterViewInit(): void {
    // Mark the view as initialized once it's fully rendered
    this.isViewInitialized = true;
  }

  private trySetDefaultAlpha(): void {
    // Only set default if selectedAlpha is still undefined (i.e., hasn't been set by store or user)
    if (!this.selectedAlpha && this.availableAlphas.length && !this.isDefaultAlphaSet && this.contentLoaded) {
      const firstLetter = this.availableAlphas.find(char => char !== '#');
      this.selectedAlpha = firstLetter || '#';

      // Dispatch to store to update the selected alpha value
      this.store.dispatch(new SelectedAlphabar(this.identifier, this.selectedAlpha));
      this.selectedAlphaEvent.emit(this.selectedAlpha);

      this.isDefaultAlphaSet = true; // Mark default alpha as set
    }
  }

  getItemFromStore(): void { 
    if (this.identifier && !this.isStoreHandled && this.contentLoaded) {
      this.store.select(state => state.alphabetSelection)
        .pipe(take(1)) // Take only the first emitted value and complete
        .subscribe(alphabetState => {
          const storedAlpha = alphabetState?.selectedAlphas?.[this.identifier];
          this.isStoreHandled = true;

          // Only update selectedAlpha if it's not already set and content is loaded
          if (storedAlpha && !this.selectedAlpha && !this.isDefaultAlphaSet) {
            this.selectedAlpha = storedAlpha;
            this.isDefaultAlphaSet = true;
            //this.selectedAlphaEvent.emit(this.selectedAlpha);
            setTimeout(() => {
              this.selectedAlphaEvent.emit(this.selectedAlpha);
            }, 1500);
          }

          // Ensure default alpha is only set once when content is loaded
          if (!this.selectedAlpha && !this.hasUserSelectedAlpha && !this.isDefaultAlphaSet) {
            this.trySetDefaultAlpha();
          }
        });
    }
  }

  alphaHasEntry(letter: string): boolean {
    return this.availableAlphas.includes(letter);
  }

  alphaSelected(letter: string): void {
    if (this.alphaHasEntry(letter) && this.contentLoaded && this.isContentLoadedChecked) {
      this.selectedAlpha = letter;
      this.hasUserSelectedAlpha = true;  // Set flag to true since user selected this letter
      this.store.dispatch(new SelectedAlphabar(this.identifier, letter));
      this.selectedAlphaEvent.emit(letter);
    }
  }

  @HostListener('document:scroll', ['$event.target'])
  onscroll() {
    if (this.identifier == 'healthorgs' || this.identifier == 'manufacturers') { 
      if (!!this.isParentPageLoaded && this.isParentPageLoaded && this.contentLoaded && this.isContentLoadedChecked) {
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrolling = currentScrollTop > this.lastScrollTop ? 1 : -1;
        this.lastScrollTop = Math.max(currentScrollTop, 0);
        this.selectButtonOnScroll(scrolling);
      }
    } 
    else { 
      if (this.contentLoaded && this.isContentLoadedChecked) {
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrolling = currentScrollTop > this.lastScrollTop ? 1 : -1;
        this.lastScrollTop = Math.max(currentScrollTop, 0);
        this.selectButtonOnScroll(scrolling);
      }
    }
  }

  selectButtonOnScroll(scrolling: number): void {
    const selector = this.identifier === 'generic' ? '.genericLetterHeading' : '.letterHeading';
    const headerElements = Array.from(document.documentElement.querySelectorAll(selector));

    for (const el of headerElements) {
      const rect = el.getBoundingClientRect();
      const visibleCondition = scrolling < 0
        ? rect.top <= this._scrollOffsetForHeader 
        && rect.bottom >= this.alphaBar.nativeElement.getBoundingClientRect().bottom
        : rect.top < window.innerHeight && rect.bottom <= window.innerHeight && 
        rect.top >= this.alphaBar.nativeElement.getBoundingClientRect().bottom;

      if (visibleCondition) {
        const id = el.getAttribute("id");
        let letter = id.replace(this.identifier, '');
        letter = letter === '0' ? '#' : (letter === ' ' ? '*' : letter);

        this.selectAlphaBarButton(letter);
      }
    }
  }

  selectAlphaBarButton(buttonId: string): void {
    if (buttonId && buttonId !== this.selectedAlpha && !this.isScrolling && this.contentLoaded && this.isContentLoadedChecked) {
      this.selectedAlpha = buttonId;
      this.store.dispatch(new SelectedAlphabar(this.identifier, buttonId));
    }
  }

  scrollToSelectedAlpha(): void {
    try {
      const id = this.identifier + this.selectedAlpha; 
      const selector = `#${CSS.escape(id)}`;
      const alphaItem = document.querySelector(selector);
      if (alphaItem) {
        alphaItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      this.changeDetector.detectChanges();
    } catch (err) {
      console.error('Invalid selector for selectedAlpha:', this.selectedAlpha, err);
    }
    setTimeout(() => {
      this.isScrolling = false;
    }, 1000);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}